import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/Reveal";
import { api, imgUrl, miles } from "@/lib/api";

const OWNERS = ["", "One owner", "Two owners", "Three owners", "Four owners", "Five owners"];
const caption = (v) => [v.previous_owners > 0 && (OWNERS[v.previous_owners] || `${v.previous_owners} owners`), v.mileage != null && miles(v.mileage)].filter(Boolean).join(" · ");

export default function SoldShowcase() {
  const [cars, setCars] = useState([]);
  const [ref, embla] = useEmblaCarousel({ align: "start", dragFree: true });

  useEffect(() => {
    api.get("/vehicles", { params: { archive: true } })
      .then((r) => {
        const list = Array.isArray(r?.data) ? r.data : [];
        setCars([...list].sort((a, b) => (b.sale_date || "").localeCompare(a.sale_date || "")));
      })
      .catch(() => {});
  }, []);

  if (!cars.length) return null;
  return (
    <section className="overflow-hidden border-t border-stone bg-stone/30 py-24 lg:py-32" data-testid="sold-showcase">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
          <Reveal>
            <SectionLabel>Previously sold</SectionLabel>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">Recently found new homes.</h2>
            <p className="mt-4 max-w-xl text-base text-charcoal/80">A record of vehicles we have selected, prepared and sold.</p>
          </Reveal>
          <div className="flex items-center gap-3">
            <button onClick={() => embla?.scrollPrev()} aria-label="Previous sold vehicles" data-testid="sold-prev" className="grid h-12 w-12 place-items-center rounded-full border border-charcoal/40 transition-colors hover:bg-forest hover:text-paper"><ArrowLeft className="h-5 w-5" strokeWidth={1.5} /></button>
            <button onClick={() => embla?.scrollNext()} aria-label="Next sold vehicles" data-testid="sold-next" className="grid h-12 w-12 place-items-center rounded-full border border-charcoal/40 transition-colors hover:bg-forest hover:text-paper"><ArrowRight className="h-5 w-5" strokeWidth={1.5} /></button>
          </div>
        </div>
        <div className="mt-14" ref={ref}>
          <div className="flex">
            {cars.map((v) => (
              <Link key={v.id} to={`/stock/${v.slug}`} data-testid={`sold-card-${v.slug}`}
                className="group min-w-0 shrink-0 grow-0 basis-[82%] pr-5 sm:basis-1/2 lg:basis-1/4">
                <div className="img-zoom relative aspect-[4/3] overflow-hidden bg-stone">
                  <img src={imgUrl(v.images?.[0])} alt={`${v.year} ${v.make} ${v.model}`} loading="lazy" draggable={false} className="h-full w-full object-cover" />
                  <span className="eyebrow absolute left-3 top-3 bg-charcoal px-2.5 py-1 !text-[0.6rem] text-paper">Sold</span>
                </div>
                <p className="eyebrow mt-4 text-charcoal/70">{v.year} · {v.make}</p>
                <p className="mt-1 font-serif text-2xl leading-tight text-charcoal">{v.model}</p>
                {v.derivative && <p className="truncate text-sm text-charcoal/75">{v.derivative}</p>}
                {caption(v) && <p className="mt-2 text-sm font-medium text-forest" data-testid={`sold-caption-${v.slug}`}>{caption(v)}</p>}
              </Link>
            ))}
          </div>
        </div>
        <Link to="/stock/sold" className="link-u mt-12 inline-block text-sm font-semibold uppercase tracking-[0.14em] text-forest" data-testid="sold-view-all">View sold archive ({cars.length})</Link>
      </div>
    </section>
  );
}
